import { useEffect, useMemo, useState } from "react";
import ChartJS from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Scatter } from "react-chartjs-2";
import axiosInstance from "../../utils/axiosInstance";
import { getSelectedBrandCode } from "../../utils/brandContext";

ChartJS.register(ChartDataLabels);

export default function OmiOliChart({
    months = 24,
    xAxisMax,
    yAxisMax,
    onSelect, // (month: string|null) => void
}) {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brandCode, setBrandCode] = useState(getSelectedBrandCode());

    const safeOnSelect = typeof onSelect === "function" ? onSelect : () => { };

    useEffect(() => {
        const syncBrand = () => setBrandCode(getSelectedBrandCode());
        window.addEventListener("brandUpdated", syncBrand);
        window.addEventListener("storage", syncBrand);
        return () => {
            window.removeEventListener("brandUpdated", syncBrand);
            window.removeEventListener("storage", syncBrand);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                setLoading(true);
                const res = await axiosInstance.get("record/records/omi-oli-graph", {
                    params: { months },
                });
                if (!cancelled) setPoints(res?.data?.points ?? []);
            } catch (_e) {
                if (!cancelled) setPoints([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [months, brandCode]);

    const chartData = useMemo(() => {
        const data = (points || [])
            .filter(
                (p) =>
                    p &&
                    typeof p.omi === "number" &&
                    Number.isFinite(p.omi)
            )
            .map((p) => ({
                x: p.omi,
                y: Number.isFinite(Number(p.oli)) ? Number(p.oli) : 0,
                month: p.month,
                month_start: p.month_start,
            }))
            // Trend in OMI space: connect left-to-right by OMI (x-axis)
            .sort((a, b) => a.x - b.x);

        return {
            datasets: [
                {
                    label: "OMI vs OLI",
                    data,
                    backgroundColor: "rgba(15, 23, 42, 0.85)", // slate-900
                    hoverBackgroundColor: "rgba(15, 23, 42, 0.95)",
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBorderWidth: 0,

                    // connect points with a line (x-ordered)
                    showLine: true,
                    borderColor: "rgba(15, 23, 42, 0.55)",
                    borderWidth: 2,
                    tension: 0.25, // set 0 for straight segments
                    spanGaps: true,
                },
            ],
        };
    }, [points]);

    const options = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            parsing: false,
            onClick: (_evt, elements) => {
                if (elements?.length > 0) {
                    const el = elements[0];
                    const ds = chartData.datasets[el.datasetIndex];
                    const pt = ds?.data?.[el.index];
                    safeOnSelect(pt?.month ?? null);
                } else {
                    safeOnSelect(null);
                }
            },
            plugins: {
                datalabels: {
                    anchor: "end",
                    align: "end",
                    offset: 2,
                    formatter: (_v, ctx) => ctx?.raw?.month ?? "",
                    font: { weight: "bold" },
                    clip: true,
                },
                title: {
                    display: true,
                    text: "OMI vs OLI (Monthly)",
                    font: { weight: "bold" },
                    padding: { top: 4, bottom: 20 },
                },
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => items?.[0]?.raw?.month || "",
                        label: (item) => {
                            const x = item?.raw?.x ?? 0;
                            const y = item?.raw?.y ?? 0;
                            return [`OMI: ${x}`, `OLI: ${Number.isFinite(y) ? y.toFixed(3) : y}`];
                        },
                    },
                },
            },
            scales: {
                x: {
                    title: { display: true, text: "OMI (sum of relevance)" },
                    beginAtZero: true,
                    max: xAxisMax,
                    ticks: { precision: 0 },
                    grid: { color: "rgba(148, 163, 184, 0.25)" },
                },
                y: {
                    title: { display: true, text: "OLI (weighted average leverage)" },
                    beginAtZero: true,
                    max: yAxisMax,
                    grid: { color: "rgba(148, 163, 184, 0.25)" },
                },
            },
        }),
        [chartData, safeOnSelect, xAxisMax, yAxisMax]
    );

    return (
        <div className="w-full p-4">
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                <div className="h-56 sm:h-72 md:h-96 lg:h-[500px]">
                    {loading ? (
                        <div className="h-full w-full rounded-xl border border-slate-200 bg-slate-50 grid place-items-center">
                            <div className="text-sm text-slate-600">Loading...</div>
                        </div>
                    ) : (
                        <Scatter data={chartData} options={options} />
                    )}
                </div>
            </div>
        </div>
    );
}
